/**
 * Vuex module for managing filter state across dashboard and orders screens.
 * Provides community-specific filter persistence using localStorage.
 */

export const state = {
  // Community-specific tracking ID filters
  // Structure: { [communityId]: { trackingIds: [], timeWindows: [] } }
  communityFilters: {},

  // Global filter state for current community
  currentTrackingIds: [],
  currentTimeWindows: ['last_48_hours', 'last_7_days', 'last_month']
}

export const getters = {
  currentTrackingIds: (state) => state.currentTrackingIds,
  currentTimeWindows: (state) => state.currentTimeWindows,
  hasActiveTrackingFilter: (state) => state.currentTrackingIds.length > 0,
  getCommunityFilters: (state) => (communityId) => {
    return state.communityFilters[communityId] || {
      trackingIds: [],
      timeWindows: ['last_48_hours', 'last_7_days', 'last_month']
    }
  }
}

export const mutations = {
  SET_TRACKING_IDS(state, trackingIds) {
    state.currentTrackingIds = Array.isArray(trackingIds) ? trackingIds : []
  },

  ADD_TRACKING_ID(state, trackingId) {
    if (!state.currentTrackingIds.includes(trackingId)) {
      state.currentTrackingIds.push(trackingId)
    }
  },

  REMOVE_TRACKING_ID(state, trackingId) {
    state.currentTrackingIds = state.currentTrackingIds.filter(id => id !== trackingId)
  },

  SET_TIME_WINDOWS(state, timeWindows) {
    state.currentTimeWindows = timeWindows
  },

  LOAD_COMMUNITY_FILTERS(state, { communityId, filters }) {
    state.communityFilters = {
      ...state.communityFilters,
      [communityId]: filters
    }
    // Apply to current state
    state.currentTrackingIds = filters.trackingIds || []
    state.currentTimeWindows = filters.timeWindows || ['last_48_hours', 'last_7_days', 'last_month']
  },

  SAVE_COMMUNITY_FILTERS(state, communityId) {
    state.communityFilters = {
      ...state.communityFilters,
      [communityId]: {
        trackingIds: state.currentTrackingIds,
        timeWindows: state.currentTimeWindows
      }
    }
  }
}

export const actions = {
  setTrackingIds({ commit, rootGetters, dispatch }, trackingIds) {
    commit('SET_TRACKING_IDS', trackingIds)
    const communityId = rootGetters['community/currentCommunityId']
    if (communityId) {
      dispatch('saveToCommunityStorage', communityId)
    }
  },

  addTrackingId({ commit, rootGetters, dispatch }, trackingId) {
    commit('ADD_TRACKING_ID', trackingId)
    const communityId = rootGetters['community/currentCommunityId']
    if (communityId) {
      dispatch('saveToCommunityStorage', communityId)
    }
  },

  removeTrackingId({ commit, rootGetters, dispatch }, trackingId) {
    commit('REMOVE_TRACKING_ID', trackingId)
    const communityId = rootGetters['community/currentCommunityId']
    if (communityId) {
      dispatch('saveToCommunityStorage', communityId)
    }
  },

  setTimeWindows({ commit, rootGetters, dispatch }, timeWindows) {
    commit('SET_TIME_WINDOWS', timeWindows)
    const communityId = rootGetters['community/currentCommunityId']
    if (communityId) {
      dispatch('saveToCommunityStorage', communityId)
    }
  },

  loadFromCommunityStorage({ commit }, communityId) {
    try {
      const key = `tracking_id_filters_${communityId}`
      const stored = localStorage.getItem(key)

      if (stored) {
        const filters = JSON.parse(stored)
        commit('LOAD_COMMUNITY_FILTERS', { communityId, filters })
      } else {
        // Check for legacy time windows storage and migrate
        const legacyTimeWindows = localStorage.getItem('dashboard_selected_time_windows')
        const timeWindows = legacyTimeWindows
          ? JSON.parse(legacyTimeWindows)
          : ['last_48_hours', 'last_7_days', 'last_month']

        // Set defaults for new community
        commit('LOAD_COMMUNITY_FILTERS', {
          communityId,
          filters: {
            trackingIds: [],
            timeWindows: timeWindows
          }
        })
      }
    } catch (error) {
      console.error('Error loading filters from localStorage:', error)
      // Set defaults on error
      commit('LOAD_COMMUNITY_FILTERS', {
        communityId,
        filters: {
          trackingIds: [],
          timeWindows: ['last_48_hours', 'last_7_days', 'last_month']
        }
      })
    }
  },

  saveToCommunityStorage({ state, commit }, communityId) {
    try {
      commit('SAVE_COMMUNITY_FILTERS', communityId)
      const key = `tracking_id_filters_${communityId}`
      const filters = {
        trackingIds: state.currentTrackingIds,
        timeWindows: state.currentTimeWindows,
        version: 1,
        lastUpdated: new Date().toISOString()
      }
      localStorage.setItem(key, JSON.stringify(filters))
    } catch (error) {
      console.error('Error saving filters to localStorage:', error)
    }
  },

  clearTrackingFilters({ commit, rootGetters, dispatch }) {
    commit('SET_TRACKING_IDS', [])
    const communityId = rootGetters['community/currentCommunityId']
    if (communityId) {
      dispatch('saveToCommunityStorage', communityId)
    }
  }
}
